/**
 * Marketing copy for the split login layout (left feature column).
 */

export const AUTH_FEATURE_PANEL_HEADING = "AI-powered videos at scale";

export const LOGIN_PAGE_TESTIMONIAL = {
  quote: "Our personalized video campaigns now convert 3× better — and our team ships them in half the time.",
  author: "Jignesh Maniar",
  role: "Onads Agency",
  initials: "JM",
};

export const LOGIN_PAGE_HERO = {
  headline: AUTH_FEATURE_PANEL_HEADING,
  subline: "Build, deliver, and measure personalized video campaigns — at the speed of AI.",
  stats: [
    { value: "10×", label: "Faster production" },
    { value: "3.2×", label: "Higher engagement" },
    { value: "500+", label: "Brands trust us" },
  ],
};

export const LOGIN_FEATURE_SECTIONS = [
  {
    title: "Built for scale",
    items: [
      {
        icon: "mdi-video-outline",
        title: "Video personalisation at scale",
        description: "Generate thousands of unique videos from one template.",
      },
      {
        icon: "mdi-chart-areaspline",
        title: "Performance analytics",
        description: "Track opens, views, engagement, and CTA clicks.",
      },
      {
        icon: "mdi-translate",
        title: "Multi-language support",
        description: "Localise videos instantly across regions and audiences.",
      },
      {
        icon: "mdi-shield-check-outline",
        title: "Enterprise-grade controls",
        description: "Templates, approvals, disclaimers, and brand governance.",
      },
      {
        icon: "mdi-database-cog-outline",
        title: "Dynamic data mapping",
        description: "Names, numbers, portfolios, regions, and languages — fully automated.",
      },
      {
        icon: "mdi-brain",
        title: "AI tools suite",
        description:
          "Best-of-class AI content tools integrated in one place to make creation convenient, yet scalable.",
      },
    ],
  },
];

/** Shared label styling for auth form fields (matches Register). */
export const AUTH_FIELD_LABEL_CLASS = "fw-semibold mb-1";

/** Google SSO + email-form divider copy (Login and Register). */
export const AUTH_GOOGLE_SSO_COPY = {
  continueWithGoogle: "Continue with Google",
  continueWithGoogleComingSoon: "Continue with Google (Coming soon)",
  continueWithEmail: "Continue with email",
  orDivider: "or",
};

export const LOGIN_PAGE_COPY = {
  pageTitle: "Welcome back",
  pageSubtitle: "Sign in to your account to continue",
  platformName: "Trustvid",
  platformLabel: "Platform",
  emailLabel: "Email",
  passwordLabel: "Password",
  emailPlaceholder: "name@company.com",
  passwordPlaceholder: "Enter your password",
  mfaCodeLabel: "Authentication code",
  mfaCodePlaceholder: "Enter 6-digit code",
  signInButton: "Sign in",
  forgotPassword: "Forgot password?",
  createAccount: "Create account",
  continueWithGoogle: AUTH_GOOGLE_SSO_COPY.continueWithGoogle,
  continueWithGoogleComingSoon: AUTH_GOOGLE_SSO_COPY.continueWithGoogleComingSoon,
  continueWithEmail: AUTH_GOOGLE_SSO_COPY.continueWithEmail,
  orDivider: AUTH_GOOGLE_SSO_COPY.orDivider,
  featurePanelHeading: AUTH_FEATURE_PANEL_HEADING,
  termsLabel: "Terms",
  privacyLabel: "Privacy",
  termsUrl: "https://trustvid.ai/terms",
  privacyUrl: "https://trustvid.ai/privacy",
  footerCopyright: (year) => `© ${year} TrustVid`,
};

// Shared auth copy used by AuthSplitLayout, AuthTrustVidLogo, and other auth components
export const AUTH_PAGE_COPY = LOGIN_PAGE_COPY;

export const REGISTER_PAGE_COPY = {
  pageTitle: "Create account",
  checkEmailHeading: "Check your email",
  cardSubtitle:
    "Start Free with starter limits and 1 Avatar template try. Verify your email, then choose Free, Premium, or Enterprise.",
  featurePanelHeading: AUTH_FEATURE_PANEL_HEADING,
  continueWithGoogle: AUTH_GOOGLE_SSO_COPY.continueWithGoogle,
  continueWithGoogleComingSoon: AUTH_GOOGLE_SSO_COPY.continueWithGoogleComingSoon,
  continueWithEmail: AUTH_GOOGLE_SSO_COPY.continueWithEmail,
  orDivider: AUTH_GOOGLE_SSO_COPY.orDivider,
};

export const FORGOT_PASSWORD_PAGE_COPY = {
  pageTitle: "Forgot password",
  cardSubtitle: "Enter the email address for your account and we'll send you a link to reset your password.",
  featurePanelHeading: AUTH_FEATURE_PANEL_HEADING,
  successMessage: "Check your inbox for a password reset link.",
  emailLabel: "Email",
  recoveryEmailPlaceholder: "name@company.com",
  resetButton: "Send reset link",
  backToSignIn: "Back to sign in",
};

export const RESET_PASSWORD_PAGE_COPY = {
  pageTitle: "Set new password",
  cardSubtitle: "Create a strong new password for your account.",
  featurePanelHeading: AUTH_FEATURE_PANEL_HEADING,
  newPasswordLabel: "New password",
  confirmPasswordLabel: "Confirm password",
  newPasswordPlaceholder: "Create a secure password",
  confirmPasswordPlaceholder: "Re-enter your password",
  resetButton: "Set new password",
  backToSignIn: "Back to sign in",
};

export const buildActorLoginWelcomeLine = (accountName) =>
  accountName?.trim()
    ? `Welcome to ${accountName.trim()}!`
    : `Welcome to ${LOGIN_PAGE_COPY.platformName}!`;

export const ACTOR_LOGIN_PAGE_COPY = {
  pageTitle: "Actor onboarding",
  featurePanelHeading: AUTH_FEATURE_PANEL_HEADING,
  cardSubtitle: "Please enter the mobile number that received the invite link.",
  mobileLabel: "Mobile number",
  mobilePlaceholder: "Enter mobile number with country code",
  sendOtpButton: "Send OTP",
  invalidMobileMessage: "Please enter a valid mobile number.",
  verifyFailedMessage: "This mobile number does not match the invite. Check the number and try again.",
  otpFailedMessage: "Unable to send OTP. Please try again.",
  unexpectedErrorMessage: "Something went wrong. Please try again later.",
};

export const ACTOR_VERIFY_PAGE_COPY = {
  pageTitle: "Verify your mobile number",
  featurePanelHeading: AUTH_FEATURE_PANEL_HEADING,
  cardSubtitle: (mobileNo) =>
    `Please enter the 6-digit code sent to ${mobileNo || "your mobile number"}.`,
  otpLabel: "Verification code",
};
